
import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FeedService } from '../../services/feedService';
import { SocialService } from '../../services/socialService';
import { getCollectionProgress } from '../../services/collectionsService';
import { unlockFraction } from '@aluvo/constants/src/collectionCompletion';
import { getMyProfile, getMyCoins } from '../../services/authService';
import NextUpCard from '../../components/NextUpCard/NextUpCard';
import spiderManCoin from '../../assets/coin_images/movies/spider-man_no_way_home.png';
import interstellarCoin from '../../assets/coin_images/movies/interstellar.png';
import collectionInfinityEssential from '../../assets/coin_images/collections/infinity_essentials.png';
import collectionNolanMastery from '../../assets/coin_images/collections/nolan_mastery.png';
import collectionTheAnimeStarterPack from '../../assets/coin_images/collections/the_anime_starter_pack.png';
import londonUnderworldCoin from '../../assets/coin_images/collections/london_underworld.png';
import cultureClassicsCoin from '../../assets/coin_images/collections/the_culture_classics.png';
import wednesdayCoin from '../../assets/coin_images/tv_shows/wednesday_s1.png';
import strangerThingsCoin from '../../assets/coin_images/tv_shows/stranger-things_s1.png';
import DemoQuiz from './demoQuiz/DemoQuiz';
import AchievementProgress from './achievementProgress/AchievementProgress';
import HomeIdentityBar from '../../components/HomeIdentityBar/HomeIdentityBar';
import MomentumStrip from '../../components/MomentumStrip/MomentumStrip';
import HomeFeed from '../../components/HomeFeed/HomeFeed';
import CollectionTensionBar from '../../components/CollectionTensionBar/CollectionTensionBar';
import TrendingVerification from '../../components/TrendingVerification/TrendingVerification';
import ContinueCollection from '../../components/ContinueCollection/ContinueCollection';
import CollectionProgressModule from '../../components/CollectionProgressModule/CollectionProgressModule';
import CollectorsToFollow from '../../components/CollectorsToFollow/CollectorsToFollow';
import styles from './Home.module.css';
import originalDarkKnightPoster from '../../assets/coin_images/movies/dark_knight_coins/originalPoster.webp';


const collectionCards = [
    { name: 'Infinity Saga Essentials', coin: collectionInfinityEssential, progressStmt: '12/12', progress: 100, required: '12 films', tone: 'gold', slug: 'infinity-saga-essentials' },
    { name: "The Anime Starter Pack", coin: collectionTheAnimeStarterPack, progressStmt: '4/8', progress: 50, required: '8 great anime', tone: 'violet', slug: 'the-anime-starter-pack' },
    { name: 'The Culture Classics', coin: cultureClassicsCoin, progressStmt: '2/5', progress: 20, required: '5 culture classics', tone: 'bronze', slug: 'the-culture-classics' },
    { name: 'The London Underworld', coin: londonUnderworldCoin, progressStmt: '3/4', progress: 75, required: '4 gritty stories', tone: 'silver', slug: 'the-london-underworld' },
    { name: 'Christopher Nolan Mastery', coin: collectionNolanMastery, progressStmt: '5/5', progress: 100, required: '5 great movies', tone: 'blue', slug: 'nolan-mastery' },
];

const challengeTitles = [
    ['The Dark Knight', 'Only 9% Earn Gold'],
    ['Breaking Bad', 'Only 6% Earn Gold'],
    ['Interstellar', 'Only 11% Earn Gold'],
    ['Spider-Man', 'Only 14% Earn Gold'],
    ['Wednesday', 'Only 18% Earn Gold'],
];

function LoggedOutHome() {
    return (
        <main className={styles.page}>
            <Helmet>
                <title>Aluvo | Movie & TV Achievements, Collectible Coins, Legendary Mastery</title>
                <meta
                    name="description"
                    content="Turn movies and TV into collectible achievements. Take challenges, earn Bronze, Silver, Gold, and Legendary coins, complete collections, and build a public trophy case."
                />
            </Helmet>

            <section className={`${styles.marketingSection} ${styles.heroMarketing}`}>
                <div className={styles.heroAura} aria-hidden="true" />
                <div className={styles.heroCopy}>
                    <p className={styles.eyebrow}>Movie & TV achievements are here</p>
                    <h1>Every Movie Fan Says They've Seen It. Can You Prove It?</h1>
                    <p className={styles.heroSubcopy}>
                        Turn movies and TV into collectible achievements. Take challenges. Earn collectible coins. Unlock Legendary. Complete themed collections. Build your public trophy case.
                    </p>
                    <div className={styles.ctaRow}>
                        <a href="/register" className={styles.primaryCta}>Start Verifying</a>
                        <a href="/collections" className={styles.secondaryCta}>Explore Collections</a>
                    </div>
                </div>

                <DemoQuiz />
            </section>

            <section className={styles.marketingSection}>
                <AchievementProgress movie={{ title: 'The Dark Knight', year: '2008', poster: originalDarkKnightPoster }} />
            </section>

            <section className={styles.marketingSection}>
                <div className={styles.sectionHeader}><p className={styles.eyebrow}>Themed mastery</p><h2>Collections Worth Completing.</h2></div>
                <div className={styles.collectionGrid}>{collectionCards.map((card) => <article className={`${styles.collectionCard} ${styles[card.tone]}`} key={card.name}><img src={card.coin} alt="" /><h3>{card.name}</h3><p>{card.required}</p><div className={styles.progressTrack} style={{ "--progress-width": `${card.progress}%` }}><span /></div><div className={styles.cardFooter}><strong>{card.progressStmt}</strong><a href={`/collections/${card.slug}`}>View Collection</a></div></article>)}</div>
            </section>

            <section className={`${styles.marketingSection} ${styles.differenceSection}`}>
                <div><p className={styles.eyebrow}>Why it feels different</p><h2>Not another place to log a movie.</h2></div>
                <div className={styles.compareGrid}><div><h3>Other Apps</h3><p>✓ Ratings</p><p>✓ Reviews</p><p>✓ Watchlists</p></div><div className={styles.aluvoCompare}><h3>Aluvo</h3><p>✓ Verification</p><p>✓ Collectible Coins</p><p>✓ Legendary Mastery</p><p>✓ Collections</p><p>✓ Trophy Case</p><p>✓ Challenges</p></div></div>
            </section>

            {/*<section className={`${styles.marketingSection} ${styles.legendarySection}`}><div className={styles.legendaryCoin}>L</div><div><p className={styles.eyebrow}>The status tier</p><h2>Legendary Isn't Given. It's Earned.</h2><p>Gold isn't the end. Earn another perfect score after the cooldown to unlock Legendary. Only the best collectors achieve it.</p></div></section>*/}

            {/*<section className={styles.marketingSection}><div className={styles.sectionHeader}><p className={styles.eyebrow}>Public proof</p><h2>Your Movie Knowledge Deserves More Than Stars.</h2></div><div className={styles.trophyCase}>{[collectionInfinityEssential, interstellarCoin, spiderManCoin, wednesdayCoin, strangerThingsCoin].map((coin, i) => <img src={coin} alt={`Trophy case coin ${i + 1}`} key={coin + i} />)}</div></section>*/}

            <section className={styles.marketingSection}><div className={styles.sectionHeader}><p className={styles.eyebrow}>Challenge yourself</p><h2>Could you score Gold on:</h2></div><div className={styles.challengeGrid}>{challengeTitles.map(([title, pct]) => <a href="/register" className={styles.challengeCard} key={title}><strong>{title}</strong><span>{pct}</span></a>)}</div></section>

            <section className={`${styles.marketingSection} ${styles.statsSection}`}><div><strong>120K+</strong><span>Movies Verified</span></div><div><strong>84K+</strong><span>Coins Earned</span></div><div><strong>7K+</strong><span>Legendary Coins</span></div><div><strong>18K+</strong><span>Collections Completed</span></div></section>

            <section className={`${styles.marketingSection} ${styles.finalCta}`}><h2>Ready To Prove What You've Watched?</h2><a href="/register" className={styles.primaryCta}>Start Verifying</a></section>
        </main>
    );
}

function Home() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [profile,     setProfile]     = useState(null);
    const [coins,       setCoins]       = useState([]);
    // For You meta: followingCount drives the follow-nobody modules below the feed.
    const [feedMeta,    setFeedMeta]    = useState(null);
    const [momentum,    setMomentum]    = useState(null);
    const [collections, setCollections] = useState([]);
    const [trending,    setTrending]    = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [collectors,  setCollectors]  = useState([]);
    const [loading,     setLoading]     = useState(true);

    const loadAll = useCallback(async () => {
        if (!user) return;
        try {
            const [
                profileData, coinsData, momentumData,
                collectionsData, trendingData, suggestionsData, collectorsData,
            ] = await Promise.allSettled([
                getMyProfile(),
                getMyCoins(),
                FeedService.getMomentum(),
                getCollectionProgress(),
                FeedService.getTrending(),
                FeedService.getOnboardingSuggestions(),
                SocialService.getUsers(),
            ]);

            if (profileData.status     === 'fulfilled') setProfile(profileData.value);
            if (coinsData.status       === 'fulfilled') setCoins(Array.isArray(coinsData.value) ? coinsData.value : []);
            if (momentumData.status    === 'fulfilled') setMomentum(momentumData.value);
            if (collectionsData.status === 'fulfilled') setCollections(collectionsData.value ?? []);
            if (trendingData.status    === 'fulfilled') setTrending(trendingData.value ?? []);
            if (suggestionsData.status === 'fulfilled') {
                const raw = suggestionsData.value?.suggestions ?? suggestionsData.value ?? [];
                setSuggestions(raw.slice(0, 4));
            }
            if (collectorsData.status === 'fulfilled') {
                const uid   = user?.id ?? user?.userId;
                const users = (collectorsData.value ?? []).filter(u => u.id !== uid).slice(0, 10);
                setCollectors(users);
            }
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (user) { loadAll(); } else { setLoading(false); }
    }, [user, loadAll]);

    if (!user) {
        return <LoggedOutHome />;
    }

    // Follows nobody yet: show the existing starter modules (incl. people to follow) under the feed.
    const isNewUser = !loading && feedMeta !== null && !feedMeta.followingCount;
    // Zero-coin activation: one specific, quiz-ready recommendation near the top.
    const coinCount = profile?.stats?.badgeCount ?? profile?.coin_count ?? coins.length;
    const isZeroCoin = !loading && Number(coinCount) === 0;

    // Collection closest to completing (highest fill %)
    const closestCollection = (() => {
        // canEarnReward (server-computed) is false for an upcoming/ended seasonal collection.
        const incomplete = collections.filter(c => !c.completed && c.total > 0 && c.canEarnReward !== false);
        if (!incomplete.length) return null;
        // Fraction toward UNLOCKING (any-N-of-M collections measure against N).
        return incomplete.reduce((best, c) => (unlockFraction(c) > unlockFraction(best) ? c : best));
    })();

    // Only trending rows that have a real title AND posterUrl
    const validTrending = trending.filter(t => t.title && t.posterUrl);

    return (
        <main className={styles.loggedInPage}>
            {/* ── 1. Identity Bar ── */}
            <HomeIdentityBar profile={profile} coins={coins} loading={loading} />

            <div className={styles.mainLayout}>
                {/* ── CENTER FEED ── */}
                <div className={styles.centerColumn}>
                    {/* ── 1b. Zero-coin Next Up: direct path to the first quiz ── */}
                    {isZeroCoin && <NextUpCard />}

                    {/* ── 2. Momentum Strip ── */}
                    <MomentumStrip
                        momentum={momentum}
                        loading={loading}
                        onCardPress={(card) => {
                            if (card.media) navigate(`/movie/${card.media.kind}/${card.media.tmdbId}`);
                            if (card.collectionSlug) navigate(`/collections/${card.collectionSlug}`);
                        }}
                    />

                    {/* ── 3. Home feed: For You / Following (docs/home-feed.md) ── */}
                    <HomeFeed myCoins={coins} onMeta={setFeedMeta} />

                    {/* ── New-user modules: only rendered when feed is empty and data exists ── */}
                    {isNewUser && suggestions.length > 0 && (
                        <ContinueCollection
                            suggestions={suggestions}
                            onEarnCoin={(s) => navigate(`/movie/${s.kind ?? s.mediaType ?? 'movie'}/${s.tmdbId ?? s.id}`)}
                        />
                    )}

                    {isNewUser && closestCollection && (
                        <CollectionProgressModule
                            collection={closestCollection}
                            onVerifyNext={(item) => navigate(`/movie/${item.kind ?? 'movie'}/${item.tmdbId}`)}
                            onViewCollection={(slug) => navigate(`/collections/${slug}`)}
                        />
                    )}

                    {isNewUser && collectors.length > 0 && (
                        <CollectorsToFollow
                            collectors={collectors}
                            currentUserId={user?.id ?? user?.userId}
                            onViewProfile={(username) => navigate(`/user/${username}`)}
                        />
                    )}
                </div>

                {/* ── RIGHT SIDEBAR ── */}
                <aside className={styles.rightSidebar}>
                    {/* ── 4. Collection Tension Bar ── */}
                    <CollectionTensionBar
                        collections={collections}
                        loading={loading}
                        onCollectionClick={(slug) => navigate(`/collections/${slug}`)}
                    />

                    {/* ── 5. Trending — loading skeleton OR valid rows only (hidden when empty) ── */}
                    {loading && (
                        <TrendingVerification trending={[]} loading={true} onVerify={() => {}} />
                    )}
                    {!loading && validTrending.length > 0 && (
                        <TrendingVerification
                            trending={validTrending}
                            loading={false}
                            onVerify={(media) => navigate(`/movie/${media.kind}/${media.tmdbId}`)}
                        />
                    )}
                </aside>
            </div>
        </main>
    );
}

export default Home;